import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from '@/components/common/theme-provider';
import { Toaster } from '@/components/ui/toaster';
import { ApiError, onMaintenanceMode } from '@/lib/api-client';
import { toastApiError } from '@/lib/api-error-toast';
import { i18n } from '@/lib/i18n';
import { MaintenancePage } from './maintenance-page';

const STALE_TIME_MS = 30_000;

function createQueryClient(): QueryClient {
  return new QueryClient({
    // Ladefehler werden zentral gemeldet; Mutationen melden ihren Fehler selbst am auslösenden Element.
    queryCache: new QueryCache({
      onError: (error) => {
        if (error instanceof ApiError && error.status === 401) {
          return;
        }
        toastApiError(error);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: STALE_TIME_MS,
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  });
}

export function AppProviders({ children }: { children: ReactNode }): JSX.Element {
  const [queryClient] = useState(createQueryClient);
  const [maintenanceError, setMaintenanceError] = useState<ApiError | null>(null);

  useEffect(() => onMaintenanceMode(setMaintenanceError), []);

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          {maintenanceError === null ? children : <MaintenancePage error={maintenanceError} />}
          <Toaster />
        </QueryClientProvider>
      </ThemeProvider>
    </I18nextProvider>
  );
}
